import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import { MultiStyleType } from '@chakra-ui/styled-system'
import { Size } from '~/types/ThemeTypes'
import { getName } from '~/utils/objectUtils'

import { phoneColorKeys } from './_colors'

const { CallSoundIndicator: CallSoundIndicatorColors } = phoneColorKeys.Phone

export enum CallSoundIndicatorVariants {
  Active = 'active',
  Hold = 'hold',
  Mute = 'mute',
  Reconnecting = 'reconnecting',
}

const sizeMap = {
  [Size.sm]: {
    fullSize: '280px',
    coreSize: '200px',
  },
  [Size.md]: {
    fullSize: '360px',
    coreSize: '240px',
  },
  [Size.lg]: {
    fullSize: '440px',
    coreSize: '280px',
  },
}

const callSoundIndicatorParts = [
  'container',
  'pulse',
  'core',
  'icon',
  'text',
] as const

const helpers = createMultiStyleConfigHelpers(callSoundIndicatorParts)

export const CallSoundIndicatorStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      background: CallSoundIndicatorColors.Container.Background,
      alignItems: 'center',
      justifyContent: 'center',
    },
    pulse: {
      borderRadius: 'full',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'pulse 1s ease-in-out infinite alternate',
    },
    core: {
      position: 'absolute',
      borderRadius: 'full',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      boxSize: '90px',
    },
    text: {
      color: CallSoundIndicatorColors.Text.FontColor,
      fontSize: '16px',
      fontWeight: 'bold',
    },
  },
  sizes: {
    [Size.sm]: {
      container: {
        boxSize: sizeMap[Size.sm].fullSize,
      },
      pulse: {
        boxSize: sizeMap[Size.sm].fullSize,
        '@keyframes pulse': {
          from: {
            height: sizeMap[Size.sm].fullSize,
            width: sizeMap[Size.sm].fullSize,
          },
          to: {
            height: sizeMap[Size.sm].coreSize,
            width: sizeMap[Size.sm].coreSize,
          },
        },
      },
      core: {
        boxSize: sizeMap[Size.sm].coreSize,
      },
      icon: {},
    },
    [Size.md]: {
      container: {
        boxSize: sizeMap[Size.md].fullSize,
      },
      pulse: {
        boxSize: sizeMap[Size.md].fullSize,
        '@keyframes pulse': {
          from: {
            height: sizeMap[Size.md].fullSize,
            width: sizeMap[Size.md].fullSize,
          },
          to: {
            height: sizeMap[Size.md].coreSize,
            width: sizeMap[Size.md].coreSize,
          },
        },
      },
      core: {
        boxSize: sizeMap[Size.md].coreSize,
      },
      icon: {},
    },
    [Size.lg]: {
      container: {
        boxSize: sizeMap[Size.lg].fullSize,
      },
      pulse: {
        boxSize: sizeMap[Size.lg].fullSize,
        '@keyframes pulse': {
          from: {
            height: sizeMap[Size.lg].fullSize,
            width: sizeMap[Size.lg].fullSize,
          },
          to: {
            height: sizeMap[Size.lg].coreSize,
            width: sizeMap[Size.lg].coreSize,
          },
        },
      },
      core: {
        boxSize: sizeMap[Size.lg].coreSize,
      },
      icon: {
        boxSize: '100px',
      },
    },
  },
  variants: {
    [CallSoundIndicatorVariants.Active]: {
      pulse: {
        background: CallSoundIndicatorColors.Active.Pulse.Background,
      },
      core: {
        background: CallSoundIndicatorColors.Active.Core.Background,
      },
      icon: {
        color: CallSoundIndicatorColors.Active.Icon.Color,
      },
    },
    [CallSoundIndicatorVariants.Hold]: {
      pulse: {
        background: CallSoundIndicatorColors.Hold.Pulse.Background,
      },
      core: {
        background: CallSoundIndicatorColors.Hold.Core.Background,
      },
      icon: {
        color: CallSoundIndicatorColors.Hold.Icon.Color,
      },
    },
    [CallSoundIndicatorVariants.Mute]: {
      pulse: {
        background: CallSoundIndicatorColors.Mute.Pulse.Background,
      },
      core: {
        background: CallSoundIndicatorColors.Mute.Core.Background,
      },
      icon: {
        color: CallSoundIndicatorColors.Mute.Icon.Color,
      },
    },
    [CallSoundIndicatorVariants.Reconnecting]: {
      pulse: {
        background: CallSoundIndicatorColors.Reconnecting.Pulse.Background,
      },
      core: {
        background: CallSoundIndicatorColors.Reconnecting.Core.Background,
      },
      icon: {
        color: CallSoundIndicatorColors.Reconnecting.Icon.Color,
        boxSize: '60px',
        margin: '10px',
      },
    },
  },
  defaultProps: {
    size: Size.sm,
    variant: CallSoundIndicatorVariants.Active,
  },
})

export const useCallSoundIndicatorStyles = (props?: StyleThemingProps) => {
  return useMultiStyleConfig(
    getName({ CallSoundIndicatorStyle }),
    props,
  ) as MultiStyleType<typeof callSoundIndicatorParts>
}
